import { Backdrop, Box, Fade, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import React, { memo } from 'react';

import { DEFAULT_LOADING_CONFIG, LOADING_ANIMATION, LOADING_ARIA, LOADING_Z_INDEX } from '../constants';
import { LoadingOverlayProps } from '../types';
import LoadingSpinner from './LoadingSpinner';

/* eslint-disable react/prop-types */

/**
 * Loading overlay component that covers the entire application
 *
 * Features:
 * - Full-screen backdrop with appropriate opacity
 * - Responsive design (adapts to mobile/desktop)
 * - Smooth fade transitions
 * - Accessibility support
 * - Theme integration
 * - Configurable spinner and message
 */
const LoadingOverlay = memo<LoadingOverlayProps>(({ open, message, spinner = {}, backdrop = {} }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { invisible = false, timeout = LOADING_ANIMATION.FADE_TIMEOUT } = backdrop;

  const { size = isMobile ? 'medium' : 'large', color = DEFAULT_LOADING_CONFIG.COLOR, ...spinnerProps } = spinner;

  return (
    <Backdrop
      open={open}
      invisible={invisible}
      timeout={timeout}
      sx={{
        zIndex: LOADING_Z_INDEX.BACKDROP,
        backgroundColor: invisible ? 'transparent' : `rgba(0, 0, 0, ${LOADING_ANIMATION.BACKDROP_OPACITY})`,
        backdropFilter: invisible ? 'none' : 'blur(2px)',
        // Ensure it covers everything
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        // Prevent scrolling when loading
        overflow: 'hidden'
      }}
      aria-live={LOADING_ARIA.LIVE_REGION}
      aria-busy={open}
      role="dialog"
      aria-label="Cargando aplicación"
    >
      <Fade in={open} timeout={timeout}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: theme.spacing(3),
            zIndex: LOADING_Z_INDEX.SPINNER,
            // Responsive padding
            px: theme.spacing(isMobile ? 2 : 4),
            py: theme.spacing(isMobile ? 3 : 4),
            // Ensure content is centered
            textAlign: 'center',
            maxWidth: isMobile ? '90vw' : '400px',
            width: '100%'
          }}
        >
          <LoadingSpinner size={size} color={color} {...spinnerProps} />

          {message && (
            <Typography
              variant={isMobile ? 'body1' : 'h6'}
              color="common.white"
              sx={{
                fontWeight: 500,
                textShadow: '0 1px 3px rgba(0,0,0,0.3)',
                // Responsive font size
                fontSize: isMobile ? theme.typography.body1.fontSize : theme.typography.h6.fontSize,
                lineHeight: 1.4,
                // Ensure text is readable
                wordBreak: 'break-word',
                hyphens: 'auto'
              }}
              aria-live={LOADING_ARIA.LIVE_REGION}
            >
              {message}
            </Typography>
          )}
        </Box>
      </Fade>
    </Backdrop>
  );
});

LoadingOverlay.displayName = 'LoadingOverlay';

export default LoadingOverlay;
